import { forwardRef, useEffect, useRef } from 'react';
import { CSSTransition } from 'react-transition-group';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';

import { Typography } from '@packages/shared/src/components/Typography/Typography';
import { Button } from '@packages/shared/src/ui/Button/Button';
import { Box } from '@packages/shared/src/components/Box/Box';

export type ResponsiveFlyoutHeaderProps = {
  onClose?: () => void;
  onBack?: () => void;
  title?: string;
  isVisible?: boolean;
};

const messages = defineMessages({
  back: {
    id: 'header.navigation.flyout.buttons.back',
    defaultMessage: 'Zurück',
  },
  close: {
    id: 'header.navigation.flyout.buttons.close',
    defaultMessage: 'Schließen',
  },
});

/**
 * Header for the navigation popup, displaying the currently selected category, and some controls to navigate back and close the popup.
 */
export const ResponsiveFlyoutHeader = forwardRef<HTMLButtonElement, ResponsiveFlyoutHeaderProps>(
  ({ title, onClose, onBack, isVisible }, ref) => {
    const closeButtonRef = useRef<HTMLButtonElement>(null);

    useEffect(() => {
      isVisible && closeButtonRef.current?.focus();
    }, [isVisible]);

    useEffect(() => {
      const onMainNavHandler = (e: KeyboardEvent) => {
        if (e.key === 'Backspace') onBack?.();
        if (e.key === 'Escape') onClose?.();
      };
      document.addEventListener('keydown', onMainNavHandler);

      return () => {
        document.removeEventListener('keydown', onMainNavHandler);
      };
    }, [onBack, onClose]);

    const { formatMessage } = useIntl();

    return (
      <Box
        sx={{
          display: { xs: 'none', lg: 'grid' },
          padding: 1,
          alignItems: 'center',
          gridTemplate: '"full" 1fr / auto',
        }}
      >
        <CSSTransition
          appear
          in={!!onBack}
          timeout={125}
          classNames={{
            enter: 'translate-x-2 opacity-0',
            enterActive: 'translate-x-0 opacity-0',
            enterDone: 'translate-x-0 opacity-100',
            exit: 'translate-x-2 opacity-0',
            exitActive: 'translate-x-2 opacity-0',
          }}
          unmountOnExit
          nodeRef={ref}
        >
          <Button
            ref={ref}
            className="start-button typography-h5 col-span-full row-span-full translate-x-2 justify-self-start border-none opacity-0 transition-[opacity,translate] duration-125 ease-in-out"
            onClick={onBack}
          >
            <span className="icon-emp-arrow-medium-left size-4" />
            <span className="text-size-h5">{formatMessage(messages.back)}</span>
          </Button>
        </CSSTransition>
        <header className="justify-self-center [grid-area:full]">
          <span className="text-size-h4">{title}</span>
        </header>
        <Button
          className="end-button typography-h5 col-span-full row-span-full justify-self-end border-none"
          ref={closeButtonRef}
          onClick={() => {
            onClose?.();
          }}
        >
          <FormattedMessage key="close-text" {...messages.close} />
          <span className="icon-emp-cancel size-4" />
        </Button>
      </Box>
    );
  },
);
